import { useEffect, useState } from 'react';
import Raven from 'raven-js';
import { useQuery } from '@apollo/client';
import { FETCH_INVOICE } from '../graphQL/invoiceHighlightQuery';
import { parseInvoice } from '../graphQL/invoiceHighlightQueryParser';
import InvoiceHighlightDetails from './InvoiceHighlightDetails';
import InvoiceHighlightActions from './InvoiceHighlightActions';
import NonNativeInvoiceHighlight from './NonNativeInvoiceHighlight';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { getPaymentStatus } from '../api/invoiceAPI';
import { getInvoiceSettings } from 'invoices-ui-lib/api/SettingsAPI';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getIsNativeInvoice = invoice => getInvoiceProperty(invoice, 'hs_invoice_source') === 'native_invoice';
const getIsInOpenStatus = invoice => getInvoiceProperty(invoice, 'hs_invoice_status') === 'open';
const StyledHighlight = styled.div.withConfig({
  displayName: "InvoiceHighlight__StyledHighlight",
  componentId: "sc-1vlvu58-0"
})(["border-bottom:1px solid ", ";padding:24px 24px 0 24px;"], BATTLESHIP);
export const HighlightLoadingSpinner = ({
  testIdSelector
}) => /*#__PURE__*/_jsx(StyledHighlight, {
  "data-test-id": testIdSelector,
  children: /*#__PURE__*/_jsx(UILoadingSpinner, {
    grow: true,
    minHeight: 128
  })
});
const InvoiceHighlight = props => {
  const {
    objectId,
    permissions,
    closeSidebar,
    onObjectUpdate,
    onObjectCreate,
    updatePreview
  } = props;
  const [paymentStatus, setPaymentStatus] = useState(undefined);
  const [canEditOpenInvoice, setCanEditOpenInvoice] = useState(false);
  const {
    data
  } = useQuery(FETCH_INVOICE, {
    variables: {
      invoiceId: objectId
    },
    fetchPolicy: 'network-only',
    onError: apolloError => {
      Raven.captureException(apolloError);
    }
  });
  const invoice = data ? parseInvoice(data.crmObject) : null;
  const isNativeInvoice = invoice ? getIsNativeInvoice(invoice) : null;
  const isOpenStatus = invoice ? getIsInOpenStatus(invoice) : null;
  const invoicePropertyDefinitions = data ? Object.values(data.crmObject.allProperties).map(property => property.definition).filter(property => Boolean(property)) : null;
  useEffect(() => {
    if (isNativeInvoice && isOpenStatus) {
      getPaymentStatus(objectId).then(setPaymentStatus).catch(error => {
        Raven.captureException(error);
      });
    }
  }, [isNativeInvoice, isOpenStatus, objectId]);
  useEffect(() => {
    getInvoiceSettings().then(settings => setCanEditOpenInvoice(settings.canEditOpenInvoice)).catch(() => {});
  }, []);
  if (!invoice || !invoicePropertyDefinitions) {
    return /*#__PURE__*/_jsx(HighlightLoadingSpinner, {
      testIdSelector: "invoice-loader"
    });
  }
  if (!isNativeInvoice) {
    return /*#__PURE__*/_jsx(NonNativeInvoiceHighlight, {
      invoice: invoice,
      propertyDefinitions: invoicePropertyDefinitions
    });
  }
  if (isOpenStatus && !paymentStatus) {
    return /*#__PURE__*/_jsx(HighlightLoadingSpinner, {
      testIdSelector: "payment-status-loader"
    });
  }
  return /*#__PURE__*/_jsxs(StyledHighlight, {
    children: [/*#__PURE__*/_jsx(InvoiceHighlightActions, {
      invoice: invoice,
      canEditInvoice: permissions.canEditSubject,
      closeSidebar: closeSidebar,
      paymentStatus: paymentStatus,
      onObjectUpdate: onObjectUpdate,
      onObjectCreate: onObjectCreate,
      updatePreview: updatePreview,
      canEditOpenInvoice: canEditOpenInvoice,
      gates: permissions.gates
    }), /*#__PURE__*/_jsx(InvoiceHighlightDetails, {
      invoice: invoice,
      propertyDefinitions: invoicePropertyDefinitions
    })]
  });
};
export default InvoiceHighlight;